import React, { useState } from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useFragment } from 'react-relay-offline'
import { last } from 'ramda'

import {
  SalesAssociateMovementsTrackingModalContent,
  SalesAssociateMovementsTrackingModalFooter,
  SalesAssociateMovementsTrackingModalHeader,
} from '../SalesAssociateMovementsTrackingModal'
import { OccurrencesBreadcrumb } from './OccurrencesBreadcrumb'
import { OccurrencesModalTitle } from './OccurrencesModalTitle'
import type { Occurrence } from '../../shared/types'
import { ModalContentTemplate } from '../../shared/types'
import type { SalesAssociatesContentTemplate_salesAssociate$key } from './__generated__/SalesAssociatesContentTemplate_salesAssociate.graphql'
import { usePositionsLists } from '../../states'
import { SalesAssociatesContentTemplateItem } from './SalesAssociatesContentTemplateItem'
import { useOccurrencesModal } from '../../hooks/useOccurrencesModal'

type SalesAssociateData = {
  id: string
  name: string | null
}

export type SalesAssociatesContentTemplateProps = {
  salesAssociateRef: SalesAssociatesContentTemplate_salesAssociate$key
}

export const SalesAssociatesContentTemplate = ({
  salesAssociateRef,
}: SalesAssociatesContentTemplateProps) => {
  const { outOfStorePositions } = usePositionsLists()
  const {
    options,
    selectedOccurrences,
    setSelectedOccurrences,
    setModalContentTemplate,
    setBreadcrumbOccurrences,
    setDisplayedOccurrences,
    breadcrumbOccurrences,
    close,
  } = useOccurrencesModal()

  const [
    selectedSalesAssociate,
    setSelectedSalesAssociate,
  ] = useState<SalesAssociateData>()

  const inServiceSalesAssociateData = useFragment<SalesAssociatesContentTemplate_salesAssociate$key>(
    graphql`
      fragment SalesAssociatesContentTemplate_salesAssociate on SalesAssociate {
        name
        avatar {
          url
        }
      }
    `,
    salesAssociateRef
  )

  const handleConfirm = () => {
    const salesAssociateOccurrence: Occurrence = {
      record: {
        id: selectedSalesAssociate?.id,
        name: selectedSalesAssociate?.name,
        reference: 'SALES_ASSOCIATE',
      },
      children: last(selectedOccurrences)?.children[0].children ?? [],
      treeType: 'POST_OPPORTUNITY',
    }

    setSelectedOccurrences((prevOccurrences) => [
      ...prevOccurrences,
      salesAssociateOccurrence,
    ])

    setBreadcrumbOccurrences((prevOccurrences) => [
      ...prevOccurrences,
      salesAssociateOccurrence,
    ])

    setModalContentTemplate(ModalContentTemplate.Occurrences)
  }

  const handleSalesAssociateClick = (
    salesAssociateData: SalesAssociateData
  ) => {
    if (selectedSalesAssociate?.id === salesAssociateData.id) {
      handleConfirm()

      return
    }

    setSelectedSalesAssociate(salesAssociateData)
  }

  const handleConfirmClick = () => {
    if (selectedSalesAssociate?.id) {
      handleConfirm()
    }
  }

  const handleBackClick = () => {
    const prevSelectedOccurrences = selectedOccurrences.slice(0, -1)
    const lastOccurrenceChildren = last(prevSelectedOccurrences)?.children ?? []

    setSelectedOccurrences(prevSelectedOccurrences)
    setBreadcrumbOccurrences(prevSelectedOccurrences)
    setDisplayedOccurrences(lastOccurrenceChildren)
    setModalContentTemplate(ModalContentTemplate.Occurrences)
  }

  return (
    <>
      <SalesAssociateMovementsTrackingModalHeader
        avatarUrl={inServiceSalesAssociateData.avatar?.url}
        sellerName={inServiceSalesAssociateData.name}
        timerDate={options.serviceStartDate}
      >
        <OccurrencesBreadcrumb occurrences={breadcrumbOccurrences} />
        <OccurrencesModalTitle
          title={options.modalTitle}
          onClick={handleBackClick}
          showGoBack={breadcrumbOccurrences.length > 0}
        />
      </SalesAssociateMovementsTrackingModalHeader>
      <SalesAssociateMovementsTrackingModalContent>
        {outOfStorePositions.map((outOfStorePosition) => (
          <SalesAssociatesContentTemplateItem
            key={outOfStorePosition.salesAssociate.id}
            onClick={handleSalesAssociateClick}
            salesAssociateRef={outOfStorePosition.salesAssociate}
            selected={
              selectedSalesAssociate?.id ===
              outOfStorePosition.salesAssociate.id
            }
          />
        ))}
      </SalesAssociateMovementsTrackingModalContent>
      <SalesAssociateMovementsTrackingModalFooter
        onCancelClick={close}
        onConfirmClick={handleConfirmClick}
        shouldDisableConfirmButton={!selectedSalesAssociate?.id}
      />
    </>
  )
}
